import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Shield, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { z } from "zod";
import { logAuthEvent, sha256Hex } from "@/lib/auditLog";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type Stage = "credentials" | "mfa" | "recovery";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState<Stage>("credentials");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();

  const finishLogin = async () => {
    logAuthEvent("login_success", { email });

    // Org MFA enforcement: redirect admins/owners without TOTP
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const hasTotp = (factors?.totp ?? []).some((f) => f.status === "verified");
      if (!hasTotp) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("organization_id")
            .eq("user_id", user.id)
            .maybeSingle();
          if (profile?.organization_id) {
            const { data: org } = await supabase
              .from("organizations")
              .select("require_mfa_for_admins")
              .eq("id", profile.organization_id)
              .maybeSingle();
            const { data: member } = await supabase
              .from("organization_members")
              .select("role")
              .eq("organization_id", profile.organization_id)
              .eq("user_id", user.id)
              .eq("status", "active")
              .maybeSingle();
            if (
              org?.require_mfa_for_admins &&
              member &&
              (member.role === "owner" || member.role === "admin")
            ) {
              toast({
                title: "MFA required",
                description: "Your organization requires admins to enable two-factor authentication.",
              });
              navigate("/settings/security");
              return;
            }
          }
        }
      }
    } catch (e) {
      console.warn("[login] mfa enforcement check failed", e);
    }

    navigate("/dashboard");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      toast({ title: "Validation Error", description: result.error.errors[0].message, variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      logAuthEvent("login_failure", { email, details: { reason: error.message } });
      toast({ title: "Login failed", description: error.message, variant: "destructive" });
      return;
    }

    // Check MFA requirement
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.nextLevel === "aal2" && aal.currentLevel !== "aal2") {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totp = (factors?.totp ?? []).find((f) => f.status === "verified");
      if (totp) {
        setFactorId(totp.id);
        setStage("mfa");
        setLoading(false);
        return;
      }
    }

    setLoading(false);
    await finishLogin();
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId || mfaCode.length !== 6) return;
    setLoading(true);
    const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({ factorId });
    if (chalErr) {
      setLoading(false);
      toast({ title: "MFA error", description: chalErr.message, variant: "destructive" });
      return;
    }
    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: chal.id,
      code: mfaCode,
    });
    setLoading(false);
    if (verErr) {
      logAuthEvent("mfa_challenge_failure", { email });
      toast({ title: "Invalid code", description: verErr.message, variant: "destructive" });
      setMfaCode("");
      return;
    }
    await finishLogin();
  };

  const handleRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = recoveryCode.trim().toUpperCase();
    if (code.length < 6) {
      toast({ title: "Invalid code", description: "Enter your 8-character recovery code.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const hash = await sha256Hex(code);
    const { data, error } = await supabase.rpc("verify_recovery_code", { _code_hash: hash });
    setLoading(false);
    if (error || !data) {
      toast({ title: "Recovery code rejected", description: "This code is invalid or already used.", variant: "destructive" });
      return;
    }
    toast({ title: "Recovery code accepted", description: "Re-enroll a new authenticator from Settings → Security." });
    // Note: AAL elevation via recovery code isn't possible client-side; user proceeds at AAL1 and is prompted to re-enroll.
    await finishLogin();
  };

  if (stage === "mfa" || stage === "recovery") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <KeyRound className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="font-display text-2xl">
              {stage === "mfa" ? "Two-factor authentication" : "Use a recovery code"}
            </CardTitle>
            <CardDescription>
              {stage === "mfa"
                ? "Enter the 6-digit code from your authenticator app."
                : "Enter one of your one-time recovery codes."}
            </CardDescription>
          </CardHeader>
          {stage === "mfa" ? (
            <form onSubmit={handleMfaVerify}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Authenticator code</Label>
                  <Input
                    id="mfa-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    autoFocus
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button type="submit" className="w-full" disabled={loading || mfaCode.length !== 6}>
                  {loading && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Verify
                </Button>
                <button type="button" onClick={() => setStage("recovery")} className="text-sm text-primary hover:underline">
                  Use a recovery code instead
                </button>
              </CardFooter>
            </form>
          ) : (
            <form onSubmit={handleRecovery}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recovery-code">Recovery code</Label>
                  <Input
                    id="recovery-code"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    className="font-mono tracking-wider"
                    maxLength={12}
                    autoFocus
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-3">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Continue
                </Button>
                <button type="button" onClick={() => setStage("mfa")} className="text-sm text-primary hover:underline">
                  Back to authenticator code
                </button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="font-display text-2xl">Welcome back</CardTitle>
          <CardDescription>Sign in to your CredentialEdge account</CardDescription>
        </CardHeader>
        <form onSubmit={handleLogin}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Signing in…" : "Sign In"}
            </Button>
            <div className="text-sm text-muted-foreground text-center space-y-1">
              <Link to="/reset-password" className="text-primary hover:underline block">Forgot password?</Link>
              <p>Don't have an account? <Link to="/signup" className="text-primary hover:underline">Start free trial</Link></p>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
